import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiCheckCircle,
  FiTag,
  FiArrowLeft
} from "react-icons/fi";
import { FaClock } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";

const ShiftPage = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(null);
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/v1/shift`;
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [lastPage, setLastPage] = useState(1);
  const [totalData, setTotalData] = useState(0);

  useEffect(() => {
    const role = (localStorage.getItem("role") || "").toLowerCase();
    setUserRole(role);
    if (role && !["owner", "admin", "superadmin", "manager"].includes(role)) {
      setShowAccessDenied(true);
    }
  }, []);

  const fetchShifts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_URL}?page=${page}&per_page=${perPage}&order_by=${sortOption}&search=${encodeURIComponent(searchTerm)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const resData = response.data;
      const rawList = Array.isArray(resData?.data)
        ? resData.data
        : Array.isArray(resData?.data?.data)
        ? resData.data.data
        : [];

      setShifts(rawList);

      if (resData.meta) {
        setLastPage(resData.meta.last_page || 1);
        setTotalData(resData.meta.total || rawList.length);
      } else {
        setLastPage(Math.ceil(rawList.length / perPage) || 1);
        setTotalData(rawList.length);
      }
    } catch (err) {
      console.error("Error fetch shifts:", err);
      setError(err.response?.data?.message || err.message || "Gagal mengambil data shift.");
      setShifts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, [page, perPage, sortOption, searchTerm]);

  const handleDelete = async (shift) => {
    const result = await Swal.fire({
      title: `Hapus Shift "${shift.name}"?`,
      text: "Tindakan ini tidak dapat dibatalkan!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
      customClass: { popup: "rounded-3xl" },
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}/${shift.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await Swal.fire({
        title: "Terhapus!",
        text: `Shift "${shift.name}" berhasil dihapus.`,
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });

      fetchShifts();
    } catch (err) {
      Swal.fire({
        title: "Gagal Menghapus",
        text: err.response?.data?.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    }
  };

  const getCodeBadge = (code) => {
    const codeUpper = (code || "").toUpperCase();
    if (codeUpper === "P") {
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    } else if (codeUpper === "S") {
      return "bg-amber-50 text-amber-700 border-amber-200";
    } else if (codeUpper === "L") {
      return "bg-rose-50 text-rose-700 border-rose-200";
    } else {
      return "bg-blue-50 text-blue-700 border-blue-200";
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* 1. TOP METRIC SUMMARY BAR */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Shift Terdaftar
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {loading ? "..." : totalData}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FiClock className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Shift Pagi (P)
            </p>
            <h2 className="text-3xl font-extrabold text-emerald-600">
              {loading ? "..." : shifts.filter((s) => (s.code || "").toUpperCase() === "P").length}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiCheckCircle className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Shift Siang & Libur
            </p>
            <h2 className="text-3xl font-extrabold text-amber-600">
              {loading ? "..." : shifts.filter((s) => ["S", "L"].includes((s.code || "").toUpperCase())).length}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-amber-50 text-amber-600 border border-amber-100">
            <FiTag className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* 2. MAIN CONTROLS & TABLE CARD */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Search Bar */}
          <div className="relative w-full md:w-80">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari nama shift atau kode..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all font-semibold"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
            {/* Sort Select */}
            <select
              value={sortOption}
              onChange={(e) => {
                setSortOption(e.target.value);
                setPage(1);
              }}
              className="p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
            >
              <option value="">Urutkan Data</option>
              <option value="asc">Nama Shift (A-Z)</option>
              <option value="desc">Nama Shift (Z-A)</option>
            </select>

            {/* Create CTA Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/enterprise/shift/create-shift")}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-teal-600/20 transition-all"
            >
              <FiPlus className="w-4 h-4" />
              <span>Buat Shift</span>
            </motion.button>
          </div>
        </div>

        {/* TABLE CONTENT */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-center text-rose-600 text-xs font-semibold bg-rose-50 rounded-2xl border border-rose-100">
            {error}
          </div>
        ) : shifts.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            <FiClock className="w-12 h-12 mx-auto mb-2 text-gray-300 stroke-1" />
            <p className="font-semibold text-gray-600">Belum ada data shift</p>
            <p className="text-xs text-gray-400 mt-1">Klik tombol 'Buat Shift' untuk menambahkan jadwal baru.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5 w-12 text-center">No</th>
                  <th className="p-3.5">Nama Shift</th>
                  <th className="p-3.5 text-center">Kode</th>
                  <th className="p-3.5">Jam Masuk</th>
                  <th className="p-3.5">Jam Pulang</th>
                  <th className="p-3.5 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shifts.map((shift, index) => (
                  <tr key={shift.id} className="hover:bg-teal-50/20 transition-colors">
                    <td className="p-3.5 text-center font-semibold text-gray-400">
                      {(page - 1) * perPage + index + 1}
                    </td>
                    <td className="p-3.5 font-bold text-gray-900 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-xl bg-teal-100 text-teal-700 font-bold flex items-center justify-center text-xs flex-shrink-0">
                        <FiClock className="w-4 h-4" />
                      </div>
                      <span>{shift.name}</span>
                    </td>
                    <td className="p-3.5 text-center">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-bold border ${getCodeBadge(shift.code)}`}
                      >
                        {shift.code}
                      </span>
                    </td>
                    <td className="p-3.5 font-semibold text-gray-700">
                      {shift.start_time || "-"}
                    </td>
                    <td className="p-3.5 font-semibold text-gray-700">
                      {shift.end_time || "-"}
                    </td>
                    <td className="p-3.5 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => navigate(`/enterprise/shift/edit-shift/${shift.id}`)}
                          className="p-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-600 transition-all hover:scale-105"
                          title="Edit Shift"
                        >
                          <FiEdit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(shift)}
                          className="p-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 transition-all hover:scale-105"
                          title="Hapus Shift"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION CONTROLS */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Menampilkan <span className="font-bold text-gray-700">{shifts.length}</span> dari{" "}
            <span className="font-bold text-gray-700">{totalData}</span> shift
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                page === 1
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-600 px-2">
              Halaman {page} dari {lastPage}
            </span>
            <button
              onClick={() => setPage((prev) => Math.min(prev + 1, lastPage))}
              disabled={page === lastPage}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                page === lastPage
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* ACCESS DENIED OVERLAY */}
      {showAccessDenied && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded-3xl shadow-2xl w-full max-w-sm text-center space-y-4">
            <h2 className="text-xl font-bold text-gray-900">Akses Ditolak</h2>
            <p className="text-xs text-gray-500">
              Anda tidak memiliki izin untuk mengelola shift.
            </p>
            <button
              onClick={() => navigate("/")}
              className="w-full py-2.5 rounded-2xl bg-teal-600 text-white text-xs font-bold hover:bg-teal-700"
            >
              Kembali ke Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftPage;
